import React from 'react';
import PropTypes from 'prop-types';
import { flow, get, merge } from 'lodash-es';
import {
  HEADER_COMPONENT,
  LEAD_FORM_VIEWS,
  LEAD_TYPES,
  SUB_HEADER_COMPONENT,
} from 'site-modules/shared/constants/lead-form/lead-form-params';
import { LEAD_FORMS_CREATIVE_ID } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/creative-ids';
import { TITLES } from 'site-modules/shared/components/lead-form/unified-lead-form/constants/multi-digital-retail-offer-type';
import { isAMSI } from 'site-modules/shared/utils/dealer-details-utils';
import {
  enrichLeadsWithAdditionalData,
  getLeadSubmissionInfoWithoutEmail,
  getPathname,
} from 'site-modules/shared/components/lead-form/utils/utils';
import { sendMarketoThankYouEmail } from 'site-modules/shared/components/lead-form/lead-form-wrapper/lead-form-wrapper-methods';
import { getLeadFormCreativeId } from 'client/site-modules/shared/components/lead-form/utils/lead-form-creative-id';
import { UnifiedLeadFormAsync } from 'site-modules/shared/components/lead-form/unified-lead-form/unified-lead-form-async';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { VisitorModel } from 'client/data/models/visitor';
import { isZipLongDistance } from 'site-modules/shared/utils/lead-form/hide-embedded-lead-form';
import { InventoryEmptyView } from 'site-modules/shared/components/lead-form/unified-lead-form/views/empty-view/inventory-empty-view/inventory-empty-view';

function SimpleInventoryLeadFormUI({ params, options, userSet, stateCode }) {
  const isUserSetZip = !!userSet;
  const { vin, make, model, year, zipCode, radius, pageName, isNewVin, formName, dealerName, isLease, type } = params;
  const { toggleDrawer, titleText, subtitleText, submitButtonText } = options;

  const leadAdditionalData = isLease ? { interest: 'lease' } : {};

  const customConfig = {
    leadType: LEAD_TYPES.INVENTORY,
    creativeId: getLeadFormCreativeId({
      isNewVin,
      pageNameLegacy: pageName,
      formName: formName || LEAD_FORMS_CREATIVE_ID.DEALER_FORM_SRP,
    }),
    leadFormComponent: LEAD_FORM_VIEWS.SIMPLE_INVENTORY_VIEW,
    leadFormConfig: {
      submitButtonText: submitButtonText || 'Get Dealer Offer',
      headerConfig: {
        headerComponent: HEADER_COMPONENT.CONVEX,
      },
      tcpaParentDealershipName: isAMSI(dealerName) ? dealerName : null,
      title: titleText || TITLES.MULTI.HEADER_NEW.TITLE_SRP_CHECK_PRICING,
      subtitle:
        subtitleText || 'Enter your contact information to get this dealer’s offer before you head to the dealership!',
      withFormClasses: false,
      toggleDrawer,
      type,
      showLeadFormOnEmptyTargets: true,
    },
    emptyLeadFormComponent: InventoryEmptyView,
    // TODO: Remove when marketo email is triggered on backend
    processDataMethods: {
      postProcessMethods: [sendMarketoThankYouEmail],
      getLeadSubmissionInfo: flow(getLeadSubmissionInfoWithoutEmail, enrichLeadsWithAdditionalData(leadAdditionalData)),
    },
    thankYouConfig: {
      headerConfig: {
        subHeaderComponent: SUB_HEADER_COMPONENT.BASE,
        subHeader: {
          text: 'You’re All Set',
          explainingText:
            'Thanks for submitting your request for the {year} {make} {model}. Your information has been sent and the dealer should follow up shortly',
          insiderLinkOut: true,
        },
      },
    },
  };

  const optionsConfig = get(options, 'customConfig', {});
  const formProps = {
    vin,
    make,
    model,
    year,
    zipCode,
    radius,
    pathname: getPathname(options),
    customConfig: merge({}, customConfig, optionsConfig),
    showZipForm: isZipLongDistance({ isUserSetZip, zipCode, stateCode }),
  };

  return <UnifiedLeadFormAsync {...formProps} />;
}

SimpleInventoryLeadFormUI.propTypes = {
  params: PropTypes.shape({
    vin: PropTypes.string,
    make: PropTypes.string,
    model: PropTypes.string,
    year: PropTypes.number,
    zipCode: PropTypes.string,
    radius: PropTypes.number,
    pageName: PropTypes.string,
    isNewVin: PropTypes.bool,
    formName: PropTypes.string,
    dealerName: PropTypes.string,
    isLease: PropTypes.bool,
    type: PropTypes.string,
  }).isRequired,
  options: PropTypes.shape({
    toggleDrawer: PropTypes.func,
    titleText: PropTypes.string,
    subtitleText: PropTypes.string,
    submitButtonText: PropTypes.string,
  }).isRequired,
  userSet: PropTypes.string,
  stateCode: PropTypes.string,
};

export const SimpleInventoryLeadForm = connectToModel(SimpleInventoryLeadFormUI, {
  userSet: bindToPath('location.userSet', VisitorModel),
  stateCode: bindToPath('location.stateCode', VisitorModel),
});
